// Typed Proposal AI client. Mirrors the Odingard Studio Proposal AI OpenAPI
// contract for the surfaces the Studio module uses. All calls go through the
// Studio Worker proxy (`/organizations/{orgId}/proposal-ai/...`), which is the
// authorization + entitlement boundary; the browser never talks to the Proposal
// AI backend directly and never holds its service credential.
import { ApiError } from "./types";

/** A source-grounded citation into a document span. */
export interface ProposalCitation {
  attachmentId?: string;
  documentId?: string;
  page: number;
  charStart?: number;
  charEnd?: number;
  snippet: string;
}

export type VerificationState = "unverified" | "needs_review" | "verified" | "rejected";

// ----- Dashboard -----------------------------------------------------------
export interface ProposalDashboard {
  openOpportunities: number;
  pipelineValue: number;
  opportunitiesByStage: Record<string, number>;
  upcomingDeadlines: { opportunityId: string; title: string; proposalDueDate: string }[];
  bidNoBidPending: number;
  complianceCoverage: { totalRows: number; covered: number; coveragePct: number };
  criticalGaps: number;
  opportunitiesByAgency: Record<string, number>;
  opportunitiesByType: Record<string, number>;
  recentSolicitations: {
    id: string;
    title: string;
    processingStatus: string;
    createdAt: string;
  }[];
}

// ----- Opportunities -------------------------------------------------------
export interface Opportunity {
  id: string;
  title: string;
  opportunityNumber?: string | null;
  customer?: string | null;
  agency?: string | null;
  opportunityType?: string | null;
  estimatedValue?: number | null;
  stage: string;
  ownerUserId?: string | null;
  setAside?: string | null;
  naics?: string | null;
  psc?: string | null;
  contractVehicle?: string | null;
  questionsDueDate?: string | null;
  proposalDueDate?: string | null;
  periodOfPerformance?: string | null;
  placeOfPerformance?: string | null;
  bidDecision?: string;
  winProbability?: number | null;
  source?: string;
  createdBy?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface OpportunityInput {
  title: string;
  agency?: string | null;
  customer?: string | null;
  opportunityNumber?: string | null;
  opportunityType?: string;
  estimatedValue?: number | null;
  naics?: string | null;
  psc?: string | null;
  setAside?: string | null;
  contractVehicle?: string | null;
  questionsDueDate?: string | null;
  proposalDueDate?: string | null;
}

// ----- Solicitations -------------------------------------------------------
export interface Solicitation {
  id: string;
  opportunityId: string;
  title: string;
  documentType: string;
  uploadStatus: string;
  processingStatus: string;
  failureReason?: string | null;
  versionNumber: number;
  amendsSolicitationId?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface UploadFile {
  filename: string;
  mimeType: string;
  content: string;
  classification?: string;
}

export interface UploadSolicitationInput {
  title: string;
  opportunityId?: string;
  opportunity?: { title: string; agency?: string | null };
  amendsSolicitationId?: string;
  files: UploadFile[];
}

/** Summary returned by POST /solicitations/{id}/process (202). */
export interface ProcessSolicitationResult {
  solicitationId: string;
  requirements: number;
  instructions: number;
  evaluationFactors: number;
  deliverables: number;
  clauseReferences: number;
  reviewQueued: number;
  matrixRows: number;
  gaps: number;
  bidRecommendation: string;
}

export interface ComplianceRow {
  id: string;
  requirementId: string;
  identifier: string;
  requirementText: string;
  requirementType: string;
  sourceSection: string;
  coverageStatus: string;
  ownerUserId?: string | null;
  responseSection?: string | null;
  criticality: string;
  verificationState: VerificationState;
}

export interface ComplianceMatrix {
  matrixId: string;
  generatedAt: string;
  rows: ComplianceRow[];
}

export interface SectionLInstruction {
  id: string;
  identifier: string;
  text: string;
  sourceSection?: string;
}

export interface SectionMFactor {
  id: string;
  identifier: string;
  text: string;
  weight?: string | null;
}

export interface AskResult {
  questionId: string;
  answerId: string;
  grounded: boolean;
  answer: string | null;
  citations: ProposalCitation[];
  provider: string;
  promptVersion: string;
  createdAt: string;
}

// ----- Knowledge assets ----------------------------------------------------
export interface KnowledgeAsset {
  id: string;
  assetType: string;
  title: string;
  body?: string | null;
  tags: string[];
  status: string;
  approvalState: string;
  versionNumber?: number;
  createdAt?: string;
}

export const KNOWLEDGE_ASSET_TYPES = [
  "past_performance",
  "resume",
  "bio",
  "certification",
  "credential",
  "capability_statement",
  "contract_vehicle",
  "technical_content",
] as const;

// ----- Workspaces + sections ----------------------------------------------
export interface Workspace {
  id: string;
  opportunityId: string;
  solicitationId?: string | null;
  title: string;
  status: string;
  pageLimitTotal?: number | null;
  createdBy?: string;
  createdAt: string;
  updatedAt: string;
}

export interface WorkspaceProgress {
  workspaceId: string;
  totalSections: number;
  completeSections: number;
  completionPercent: number;
  totalWordCount: number;
  totalWordTarget: number;
  requirementsLinked: number;
  statusCounts: Record<string, number>;
}

export interface Section {
  id: string;
  workspaceId: string;
  parentSectionId?: string | null;
  heading: string;
  orderIndex: number;
  ownerUserId?: string | null;
  dueDate?: string | null;
  pageLimit?: number | null;
  wordCountTarget?: number | null;
  status: string;
  currentVersionId?: string | null;
  requirementIds: string[];
}

export interface SectionAssignment {
  id: string;
  sectionId: string;
  userId: string;
  role: string;
  createdAt: string;
}

export interface SectionComment {
  id: string;
  sectionId: string;
  authorUserId: string;
  body: string;
  mentions: string[];
  resolved: boolean;
  createdAt: string;
}

export interface DraftSourceReference {
  sourceType?: string;
  sourceId?: string;
  label: string;
  snippet: string;
}

export interface SectionVersion {
  id: string;
  sectionId: string;
  versionNumber: number;
  content: string;
  contentChecksum: string;
  wordCount: number;
  changeSummary?: string | null;
  authorUserId?: string;
  reviewState: string;
  approvalState: string;
  sourceReferences: DraftSourceReference[];
  restoredFromVersion?: number | null;
  aiGenerated: boolean;
  aiMetadata?: {
    provider?: string;
    promptVersion?: string;
    confidence?: number;
    grounded?: boolean;
  } | null;
  createdAt: string;
}

export interface DraftResult {
  grounded: boolean;
  version?: SectionVersion;
  content?: string;
  confidence?: number;
  sourceReferences?: DraftSourceReference[];
}

export interface SourceSelector {
  requirementIds?: string[];
  knowledgeAssetIds?: string[];
  pastPerformanceIds?: string[];
  resumeIds?: string[];
  contentBlockIds?: string[];
}

export const NARRATIVE_TYPES = [
  "executive_summary",
  "technical_approach",
  "management_approach",
  "staffing_plan",
  "transition_plan",
  "quality_plan",
] as const;

// ----- Reviews -------------------------------------------------------------
export const REVIEW_STAGES = ["blue", "pink", "red", "gold"] as const;
export type ReviewStage = (typeof REVIEW_STAGES)[number];

export const COMMENT_SEVERITIES = ["info", "minor", "major", "critical"] as const;
export const REVIEW_RESOLUTIONS = ["fixed", "wont_fix", "deferred", "duplicate"] as const;

export interface ReviewCycle {
  id: string;
  workspaceId: string;
  stage: string;
  scope?: string | null;
  status: string;
  rubric: Record<string, unknown>;
  createdBy?: string;
  createdAt: string;
  completedAt?: string | null;
}

export interface ReviewComment {
  id: string;
  cycleId: string;
  sectionId?: string | null;
  severity: string;
  status: string;
  body: string;
  ownerUserId?: string | null;
  authorUserId?: string;
  createdAt: string;
}

// ----- Approvals + compliance ----------------------------------------------
export interface Approval {
  id: string;
  workspaceId: string;
  status: string;
  requestedBy?: string;
  deciderUserId?: string | null;
  delegatedToUserId?: string | null;
  rationale?: string | null;
  requestedAt: string;
  decidedAt?: string | null;
}

export interface ComplianceValidation {
  id: string;
  workspaceId: string;
  totalFindings: number;
  criticalFindings: number;
  blocking: boolean;
  createdAt: string;
  findings: { requirementId?: string; identifier?: string; issue: string; severity: string }[];
}

export interface ComplianceSignoff {
  id: string;
  workspaceId: string;
  validationId: string;
  result: string;
  criticalFailures: number;
  exceptions: unknown[];
  signerUserId?: string;
  sealedHash: string;
  createdAt: string;
}

// ----- Submission + evidence -----------------------------------------------
export interface SubmissionArtifact {
  id: string;
  format: string;
  byteSize: number;
  checksum: string;
  versionLabel?: string;
  frozen?: boolean;
  createdAt?: string;
}

export interface SubmissionPackage {
  workspaceId: string;
  versionLabel: string;
  artifacts: SubmissionArtifact[];
  manifest: Record<string, unknown>;
  manifestChecksum: string;
}

export interface PortalValidation {
  packageId: string;
  ready: boolean;
  checks: { name: string; passed: boolean; detail?: string }[];
}

export interface EvidencePack {
  id: string;
  workspaceId: string;
  checksum: string;
  auditChainIntact: boolean;
  auditHeadHash: string;
  byteSize: number;
  content: Record<string, unknown>;
  createdAt: string;
}

export interface EvidenceVerify {
  id: string;
  checksumValid: boolean;
  auditChainIntact: boolean;
}

// ----- Proposal-to-contract continuity -------------------------------------
export interface Continuity {
  id: string;
  opportunityId: string;
  workspaceId: string;
  status: string;
  baselineChecksum: string;
  contractDraftRef?: string | null;
  humanApprovedBy?: string | null;
  humanApprovedAt?: string | null;
  errorDetail?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface FreezeBaselineInput {
  workspaceId: string;
  signoffId?: string | null;
  submissionPackageId?: string | null;
  acceptedTerms?: string[];
  commitments?: string[];
  staffing?: string[];
  deliverables?: string[];
  pricingAssumptions?: string[];
  milestones?: string[];
  complianceCommitments?: string[];
}

export interface RoiRecord {
  id: string;
  metric: string;
  value: number;
  calculationMethod: string;
  timeRange: { start: string; end: string };
  sourceRecordIds: string[];
  confidence: number;
  attributionRules: string;
  auditRef: string;
}

export interface ProposalClientOptions {
  baseUrl: string;
  getToken?: () => string | null;
  onUnauthorized?: () => void;
}

/** Result of a proxied request, including whether it was served in demo mode. */
export interface ProposalResponse<T> {
  data: T;
  /** True when the Worker answered from the deterministic fixture layer. */
  demoMode: boolean;
  correlationId: string | null;
}

export class ProposalAiClient {
  private baseUrl: string;
  private getToken: () => string | null;
  private onUnauthorized: (() => void) | undefined;

  constructor(opts: ProposalClientOptions) {
    this.baseUrl = opts.baseUrl.replace(/\/$/, "");
    this.getToken = opts.getToken ?? (() => null);
    this.onUnauthorized = opts.onUnauthorized;
  }

  private async request<T>(
    method: string,
    orgId: string,
    subpath: string,
    body?: unknown,
  ): Promise<ProposalResponse<T>> {
    const headers: Record<string, string> = { Accept: "application/json" };
    const token = this.getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
    if (body !== undefined) headers["Content-Type"] = "application/json";
    const init: RequestInit = { method, headers };
    if (body !== undefined) init.body = JSON.stringify(body);
    const path = `/organizations/${orgId}/proposal-ai/${subpath.replace(/^\/+/, "")}`;
    let res: Response;
    try {
      res = await fetch(`${this.baseUrl}${path}`, init);
    } catch {
      throw new ApiError(0, "network_error", "Unable to reach the server. Check your connection.");
    }
    if (res.status === 401 && this.onUnauthorized) this.onUnauthorized();
    const text = await res.text();
    let data: unknown = null;
    if (text) {
      try {
        data = JSON.parse(text);
      } catch {
        data = null;
      }
    }
    if (!res.ok) {
      // Preserve the Proposal AI structured error envelope { error: { code, message } }.
      const env = (data ?? {}) as { error?: { code?: string; message?: string } | string };
      if (env.error && typeof env.error === "object") {
        throw new ApiError(res.status, env.error.code ?? "error", env.error.message ?? `Request failed (${res.status}).`);
      }
      const flat = (data ?? {}) as { error?: string; message?: string };
      throw new ApiError(res.status, flat.error ?? "error", flat.message ?? `Request failed (${res.status}).`);
    }
    return {
      data: data as T,
      demoMode: res.headers.get("x-proposal-ai-mode") === "demo",
      correlationId: res.headers.get("x-correlation-id"),
    };
  }

  // ----- Dashboard + opportunities -----------------------------------------
  dashboard(orgId: string) {
    return this.request<ProposalDashboard>("GET", orgId, "dashboard");
  }
  opportunities(orgId: string) {
    return this.request<{ opportunities: Opportunity[] }>("GET", orgId, "opportunities");
  }
  opportunity(orgId: string, opportunityId: string) {
    return this.request<Opportunity>("GET", orgId, `opportunities/${opportunityId}`);
  }
  createOpportunity(orgId: string, input: OpportunityInput) {
    return this.request<Opportunity>("POST", orgId, "opportunities", input);
  }

  // ----- Solicitations ------------------------------------------------------
  solicitations(orgId: string, opportunityId?: string) {
    const q = opportunityId ? `?opportunityId=${encodeURIComponent(opportunityId)}` : "";
    return this.request<{ solicitations: Solicitation[] }>("GET", orgId, `solicitations${q}`);
  }
  solicitation(orgId: string, solicitationId: string) {
    return this.request<Solicitation>("GET", orgId, `solicitations/${solicitationId}`);
  }
  uploadSolicitation(orgId: string, input: UploadSolicitationInput) {
    return this.request<Solicitation>("POST", orgId, "solicitations", input);
  }
  processSolicitation(orgId: string, solicitationId: string) {
    return this.request<ProcessSolicitationResult>("POST", orgId, `solicitations/${solicitationId}/process`, {});
  }
  complianceMatrix(orgId: string, solicitationId: string) {
    return this.request<ComplianceMatrix>("GET", orgId, `solicitations/${solicitationId}/compliance-matrix`);
  }
  sectionL(orgId: string, solicitationId: string) {
    return this.request<{ instructions: SectionLInstruction[] }>("GET", orgId, `solicitations/${solicitationId}/section-l`);
  }
  sectionM(orgId: string, solicitationId: string) {
    return this.request<{ factors: SectionMFactor[] }>("GET", orgId, `solicitations/${solicitationId}/section-m`);
  }
  askSolicitation(orgId: string, solicitationId: string, question: string) {
    return this.request<AskResult>("POST", orgId, `solicitations/${solicitationId}/ask`, { question });
  }

  // ----- Knowledge assets ---------------------------------------------------
  knowledgeAssets(orgId: string) {
    return this.request<{ assets: KnowledgeAsset[] }>("GET", orgId, "knowledge-assets");
  }
  createKnowledgeAsset(
    orgId: string,
    input: { assetType: string; title: string; body?: string; tags?: string[] },
  ) {
    return this.request<KnowledgeAsset>("POST", orgId, "knowledge-assets", input);
  }
  approveKnowledgeAsset(orgId: string, assetId: string) {
    return this.request<KnowledgeAsset>("POST", orgId, `knowledge-assets/${assetId}/approve`, {});
  }

  // ----- Workspaces + sections ---------------------------------------------
  workspaces(orgId: string) {
    return this.request<{ workspaces: Workspace[] }>("GET", orgId, "proposal-workspaces");
  }
  workspace(orgId: string, workspaceId: string) {
    return this.request<Workspace>("GET", orgId, `proposal-workspaces/${workspaceId}`);
  }
  createWorkspace(
    orgId: string,
    input: { opportunityId: string; solicitationId?: string | null; title: string; pageLimitTotal?: number | null },
  ) {
    return this.request<Workspace>("POST", orgId, "proposal-workspaces", input);
  }
  workspaceProgress(orgId: string, workspaceId: string) {
    return this.request<WorkspaceProgress>("GET", orgId, `proposal-workspaces/${workspaceId}/progress`);
  }
  generateOutline(orgId: string, workspaceId: string) {
    return this.request<{ sections: Section[] }>("POST", orgId, `proposal-workspaces/${workspaceId}/outline`, {});
  }
  sections(orgId: string, workspaceId: string) {
    return this.request<{ sections: Section[] }>("GET", orgId, `proposal-workspaces/${workspaceId}/sections`);
  }
  createSection(orgId: string, workspaceId: string, input: { heading: string; ownerUserId?: string | null }) {
    return this.request<Section>("POST", orgId, `proposal-workspaces/${workspaceId}/sections`, input);
  }
  section(orgId: string, sectionId: string) {
    return this.request<Section>("GET", orgId, `sections/${sectionId}`);
  }
  assignSection(orgId: string, sectionId: string, userId: string, role: string) {
    return this.request<SectionAssignment>("POST", orgId, `sections/${sectionId}/assignments`, { userId, role });
  }
  sectionAssignments(orgId: string, sectionId: string) {
    return this.request<{ assignments: SectionAssignment[] }>("GET", orgId, `sections/${sectionId}/assignments`);
  }
  addSectionComment(orgId: string, sectionId: string, body: string) {
    return this.request<SectionComment>("POST", orgId, `sections/${sectionId}/comments`, { body });
  }
  sectionComments(orgId: string, sectionId: string) {
    return this.request<{ comments: SectionComment[] }>("GET", orgId, `sections/${sectionId}/comments`);
  }
  resolveSectionComment(orgId: string, commentId: string) {
    return this.request<void>("POST", orgId, `comments/${commentId}/resolve`, {});
  }
  saveSectionContent(orgId: string, sectionId: string, content: string, changeSummary?: string) {
    return this.request<SectionVersion>("POST", orgId, `sections/${sectionId}/content`, {
      content,
      ...(changeSummary !== undefined ? { changeSummary } : {}),
    });
  }
  sectionVersions(orgId: string, sectionId: string) {
    return this.request<{ versions: SectionVersion[] }>("GET", orgId, `sections/${sectionId}/versions`);
  }
  draftSection(
    orgId: string,
    sectionId: string,
    input: { narrativeType: string; action?: string; instruction?: string; sources: SourceSelector },
  ) {
    return this.request<DraftResult>("POST", orgId, `sections/${sectionId}/draft`, {
      narrativeType: input.narrativeType,
      action: input.action ?? "draft",
      ...(input.instruction !== undefined ? { instruction: input.instruction } : {}),
      sources: input.sources,
    });
  }

  // ----- Reviews ------------------------------------------------------------
  reviewCycles(orgId: string, workspaceId: string) {
    return this.request<{ cycles: ReviewCycle[] }>("GET", orgId, `workspaces/${workspaceId}/review-cycles`);
  }
  startReviewCycle(orgId: string, workspaceId: string, stage: ReviewStage, scope?: string) {
    return this.request<ReviewCycle>("POST", orgId, `workspaces/${workspaceId}/review-cycles`, {
      stage,
      ...(scope !== undefined ? { scope } : {}),
    });
  }
  reviewComments(orgId: string, cycleId: string) {
    return this.request<{ comments: ReviewComment[] }>("GET", orgId, `review-cycles/${cycleId}/comments`);
  }
  addReviewComment(orgId: string, cycleId: string, body: string, severity: string) {
    return this.request<ReviewComment>("POST", orgId, `review-cycles/${cycleId}/comments`, { body, severity });
  }
  resolveReviewComment(orgId: string, commentId: string, resolutionType: string, note?: string) {
    return this.request<ReviewComment>("POST", orgId, `review-comments/${commentId}/resolve`, {
      resolutionType,
      ...(note !== undefined ? { note } : {}),
    });
  }
  completeReviewCycle(orgId: string, cycleId: string) {
    return this.request<ReviewCycle>("POST", orgId, `review-cycles/${cycleId}/complete`, {});
  }

  // ----- Approvals + compliance --------------------------------------------
  approvals(orgId: string, workspaceId: string) {
    return this.request<{ approvals: Approval[] }>("GET", orgId, `workspaces/${workspaceId}/approvals`);
  }
  requestApproval(orgId: string, workspaceId: string, rationale?: string) {
    return this.request<Approval>("POST", orgId, `workspaces/${workspaceId}/approvals`, {
      ...(rationale !== undefined ? { rationale } : {}),
    });
  }
  decideApproval(orgId: string, approvalId: string, decision: "approved" | "rejected" | "returned", rationale?: string) {
    return this.request<Approval>("POST", orgId, `approvals/${approvalId}/decide`, {
      decision,
      ...(rationale !== undefined ? { rationale } : {}),
    });
  }
  runComplianceValidation(orgId: string, workspaceId: string) {
    return this.request<ComplianceValidation>("POST", orgId, `workspaces/${workspaceId}/compliance-validations`, {});
  }
  complianceValidations(orgId: string, workspaceId: string) {
    return this.request<{ validations: ComplianceValidation[] }>("GET", orgId, `workspaces/${workspaceId}/compliance-validations`);
  }
  signoffCompliance(orgId: string, workspaceId: string, validationId: string) {
    return this.request<ComplianceSignoff>("POST", orgId, `workspaces/${workspaceId}/compliance-signoffs`, { validationId });
  }

  // ----- Submission + evidence ---------------------------------------------
  submissionPackages(orgId: string, workspaceId: string) {
    return this.request<{ packages: SubmissionArtifact[] }>("GET", orgId, `workspaces/${workspaceId}/submission-packages`);
  }
  assembleSubmissionPackage(orgId: string, workspaceId: string, versionLabel: string, signoffId: string) {
    return this.request<SubmissionPackage>("POST", orgId, `workspaces/${workspaceId}/submission-packages`, {
      versionLabel,
      signoffId,
    });
  }
  validatePortalReadiness(orgId: string, packageId: string) {
    return this.request<PortalValidation>("POST", orgId, `submission-packages/${packageId}/portal-validation`, {});
  }
  generateEvidencePack(orgId: string, workspaceId: string) {
    return this.request<EvidencePack>("POST", orgId, `workspaces/${workspaceId}/evidence-packs`, {});
  }
  verifyEvidencePack(orgId: string, packId: string) {
    return this.request<EvidenceVerify>("GET", orgId, `evidence-packs/${packId}/verify`);
  }

  // ----- Proposal-to-contract handoff --------------------------------------
  continuity(orgId: string, opportunityId: string) {
    return this.request<Continuity>("GET", orgId, `opportunities/${opportunityId}/continuity`);
  }
  freezeBaseline(orgId: string, opportunityId: string, input: FreezeBaselineInput) {
    return this.request<Continuity>("POST", orgId, `opportunities/${opportunityId}/continuity/freeze`, input);
  }
  requestContractDraft(orgId: string, opportunityId: string) {
    return this.request<Continuity>("POST", orgId, `opportunities/${opportunityId}/continuity/request-draft`, {});
  }
  approveContinuity(orgId: string, opportunityId: string) {
    return this.request<Continuity>("POST", orgId, `opportunities/${opportunityId}/continuity/approve`, {});
  }

  // ----- Analytics ----------------------------------------------------------
  roi(orgId: string) {
    return this.request<{ records: RoiRecord[] }>("GET", orgId, "roi");
  }
}
